import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill, { Quill } from 'react-quill-new';
import QuillTableBetter from 'quill-table-better';
import 'react-quill-new/dist/quill.snow.css';
import { templatesApi } from '../../../utils/templatesApi';
import './TemplateGallery.css';

// The document editors register this at module load too; registering again
// with `true` is a harmless overwrite and keeps this modal self-sufficient.
Quill.register({ 'modules/table-better': QuillTableBetter }, true);

const CATEGORY_OPTIONS = [
    'Meetings and teamwork',
    'Planning and decisions',
    'Operations and support',
    'Personal and learning',
    'Other',
];

const VARIABLE_CHIPS = [
    ['{{date}}', 'Today, e.g. September 28, 2026'],
    ['{{date:iso}}', 'Today as 2026-09-28 (safe in titles)'],
    ['{{date+7d:iso}}', 'A week from today'],
    ['{{time}}', 'Current time'],
    ['{{weekday}}', 'Weekday name'],
    ['{{week_start:iso}}', 'Monday of this week'],
    ['{{me}}', 'Your display name'],
    ['{{workspace}}', 'Workspace name'],
];

const FIELD_TYPES = ['text', 'date', 'person', 'choice'];
const KEY_RE = /^[a-z0-9_]+$/;

const toKey = (label) => String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

const emptyTemplate = (workspaceId) => ({
    name: '',
    description: '',
    category: 'Other',
    icon: '',
    kind: 'page',
    title_pattern: '',
    description_pattern: '',
    content: '',
    fields: [],
    pages: [],
    workspace_id: workspaceId || null,
});

/**
 * Create / edit a custom template (BBD-109 / BBD-110): metadata, fill-in
 * field builder, variable chips and a Quill editor for the content. Kit
 * child pages are shown read-only here (kits are captured from a space).
 *
 * Props: isOpen, onClose, template (null = new), workspaceId (new templates
 * only), onSaved(saved)
 */
const TemplateEditorModal = ({ isOpen, onClose, template = null, workspaceId = null, onSaved }) => {
    const [draft, setDraft] = useState(emptyTemplate(workspaceId));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [newField, setNewField] = useState({ label: '', type: 'text', required: false, options: '' });
    const quillRef = useRef(null);
    const titleRef = useRef(null);
    const [lastFocus, setLastFocus] = useState('content'); // where chips insert: 'content' | 'title'

    useEffect(() => {
        if (!isOpen) return;
        setError('');
        setNewField({ label: '', type: 'text', required: false, options: '' });
        setDraft(template ? {
            ...emptyTemplate(template.workspace_id),
            ...template,
            fields: (template.fields || []).map((f) => ({ ...f })),
            pages: (template.pages || []).map((p) => ({ ...p })),
        } : emptyTemplate(workspaceId));
    }, [isOpen, template, workspaceId]);

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                [{ indent: '-1' }, { indent: '+1' }],
                ['blockquote', 'code-block', 'link'],
                ['table-better'],
                ['clean'],
            ],
        },
        table: false,
        'table-better': {
            language: 'en_US',
            menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
            toolbarTable: true,
        },
        keyboard: { bindings: QuillTableBetter.keyboardBindings },
    }), []);

    if (!isOpen) return null;

    const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    const insertToken = (token) => {
        if (lastFocus === 'title' && titleRef.current) {
            const input = titleRef.current;
            const start = input.selectionStart ?? draft.title_pattern.length;
            const end = input.selectionEnd ?? start;
            const next = draft.title_pattern.slice(0, start) + token + draft.title_pattern.slice(end);
            set('title_pattern', next);
            setTimeout(() => { input.focus(); input.setSelectionRange(start + token.length, start + token.length); }, 0);
            return;
        }
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const sel = quill.getSelection(true) || { index: quill.getLength() - 1, length: 0 };
        quill.insertText(sel.index, token, 'user');
        quill.setSelection(sel.index + token.length, 0, 'user');
    };

    const addField = () => {
        const label = newField.label.trim();
        if (!label) return;
        const taken = new Set(draft.fields.map((f) => f.key));
        let key = toKey(label) || 'field';
        let n = 2;
        while (taken.has(key)) { key = `${toKey(label)}_${n}`; n += 1; }
        if (!KEY_RE.test(key)) { setError('Field labels need at least one letter or digit.'); return; }
        if (draft.fields.length >= 10) { setError('A template can have at most 10 fields.'); return; }
        const options = newField.type === 'choice'
            ? newField.options.split(',').map((o) => o.trim()).filter(Boolean)
            : [];
        if (newField.type === 'choice' && !options.length) { setError('List the choices, separated by commas.'); return; }
        set('fields', [...draft.fields, { key, label, type: newField.type, required: newField.required, default: '', options }]);
        setNewField({ label: '', type: 'text', required: false, options: '' });
        setError('');
        insertToken(`{{field:${key}}}`);
    };

    const removeField = (key) => set('fields', draft.fields.filter((f) => f.key !== key));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!draft.name.trim()) { setError('Give the template a name.'); return; }
        setBusy(true);
        setError('');
        try {
            const payload = {
                name: draft.name.trim(),
                description: draft.description.trim(),
                category: draft.category.trim(),
                icon: (draft.icon || '').trim(),
                kind: draft.kind,
                title_pattern: draft.kind === 'snippet' ? '' : draft.title_pattern.trim(),
                description_pattern: draft.description_pattern.trim(),
                content: draft.content || '',
                fields: draft.fields,
                pages: draft.kind === 'kit' ? draft.pages : [],
            };
            let saved;
            if (template?.id) {
                saved = await templatesApi.update(template.id, payload);
            } else {
                if (draft.workspace_id) payload.workspace_id = draft.workspace_id;
                saved = await templatesApi.create(payload);
            }
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            if (err.status === 413) setError(err.data?.message || 'Storage limit exceeded.');
            else setError(err.message || 'Could not save the template.');
        } finally {
            setBusy(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="TplGallery__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
            <form className="TplGallery__modal TplEditor" onSubmit={handleSubmit} role="dialog" aria-modal="true" aria-label="Template editor">
                <div className="TplGallery__header">
                    <h3 className="TplGallery__title">{template?.id ? 'Edit template' : 'New template'}</h3>
                    <button type="button" className="TplGallery__close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="TplEditor__body">
                    <div className="TplEditor__meta">
                        <div className="TplFields__field">
                            <label htmlFor="tpl-ed-name">Name<span className="TplFields__required">*</span></label>
                            <input id="tpl-ed-name" type="text" maxLength={100} value={draft.name} onChange={(e) => set('name', e.target.value)} />
                        </div>
                        <div className="TplFields__field">
                            <label htmlFor="tpl-ed-desc">Use it when…</label>
                            <input id="tpl-ed-desc" type="text" maxLength={500} value={draft.description} onChange={(e) => set('description', e.target.value)} />
                        </div>
                        <div className="TplEditor__grid3">
                            <div className="TplFields__field">
                                <label htmlFor="tpl-ed-cat">Category</label>
                                <select id="tpl-ed-cat" value={CATEGORY_OPTIONS.includes(draft.category) ? draft.category : 'Other'} onChange={(e) => set('category', e.target.value)}>
                                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="TplFields__field">
                                <label htmlFor="tpl-ed-kind">Kind</label>
                                <select id="tpl-ed-kind" value={draft.kind} onChange={(e) => set('kind', e.target.value)} disabled={draft.kind === 'kit'}>
                                    <option value="page">Page</option>
                                    <option value="space">Space</option>
                                    <option value="snippet">Snippet (insert into a document)</option>
                                    {draft.kind === 'kit' && <option value="kit">Kit (space + pages)</option>}
                                </select>
                            </div>
                        </div>
                        {draft.kind !== 'snippet' && (
                            <div className="TplFields__field">
                                <label htmlFor="tpl-ed-title">Document title pattern</label>
                                <input
                                    id="tpl-ed-title"
                                    ref={titleRef}
                                    type="text"
                                    maxLength={150}
                                    placeholder="e.g. Meeting notes - {{date:iso}}"
                                    value={draft.title_pattern}
                                    onFocus={() => setLastFocus('title')}
                                    onChange={(e) => set('title_pattern', e.target.value)}
                                />
                                <div className="TplStart__note">Titles allow letters, digits, spaces, dots, dashes and underscores; use {'{{date:iso}}'} for dates.</div>
                            </div>
                        )}

                        <div className="TplFields__field">
                            <label>Fill-in fields</label>
                            {draft.fields.length === 0 && <div className="TplStart__note">None yet. Fields are asked for when the template is used.</div>}
                            <ul className="TplEditor__fieldList">
                                {draft.fields.map((f) => (
                                    <li key={f.key}>
                                        <button type="button" className="TplEditor__chip" title="Insert at cursor" onClick={() => insertToken(`{{field:${f.key}}}`)}>
                                            {'{{field:'}{f.key}{'}}'}
                                        </button>
                                        <span className="TplEditor__fieldLabel">{f.label} · {f.type}{f.required ? ' · required' : ''}</span>
                                        <button type="button" className="TplGallery__close" title="Remove field" onClick={() => removeField(f.key)}>×</button>
                                    </li>
                                ))}
                            </ul>
                            <div className="TplEditor__addField">
                                <input type="text" placeholder="Field label, e.g. Attendees" value={newField.label} maxLength={100} onChange={(e) => setNewField({ ...newField, label: e.target.value })} />
                                <select value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })} aria-label="Field type">
                                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="TplEditor__addFieldActions">
                                <label className="TplEditor__inlineCheck">
                                    <input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} /> Required
                                </label>
                                <button type="button" className="TplGallery__btn" onClick={addField}>Add field</button>
                            </div>
                            {newField.type === 'choice' && (
                                <input type="text" placeholder="Choices, comma separated" value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} style={{ marginTop: 6 }} />
                            )}
                        </div>

                        <div className="TplFields__field">
                            <label>Variables</label>
                            <div className="TplEditor__chips">
                                {VARIABLE_CHIPS.map(([token, tip]) => (
                                    <button type="button" key={token} className="TplEditor__chip" title={tip} onClick={() => insertToken(token)}>{token}</button>
                                ))}
                            </div>
                            <div className="TplStart__note">Chips insert into the content, or into the title pattern when it was focused last.</div>
                        </div>

                        {draft.kind === 'kit' && (
                            <div className="TplFields__field">
                                <label>Kit pages</label>
                                <ul className="TplGallery__tree">
                                    {draft.pages.map((p) => (
                                        <li key={p.key} className={p.parent_key ? 'is-child' : ''}><i className="fas fa-file-lines TplGallery__treeIcon" aria-hidden="true"></i>{p.name_pattern}</li>
                                    ))}
                                </ul>
                                <div className="TplStart__note">Page bodies are captured from the original space and edited by re-saving it as a template.</div>
                            </div>
                        )}
                    </div>

                    <div className="TplEditor__content" onFocusCapture={() => setLastFocus('content')}>
                        <label className="TplEditor__contentLabel">{draft.kind === 'kit' ? 'Space content' : 'Content'}</label>
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={draft.content}
                            onChange={(html) => set('content', html)}
                            modules={modules}
                        />
                    </div>
                </div>
                {error && <div className="TplGallery__error">{error}</div>}
                <div className="TplGallery__previewFoot">
                    <button type="button" className="TplGallery__btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="submit" className="TplGallery__btn TplGallery__btn--primary" disabled={busy}>
                        {busy ? 'Saving…' : (template?.id ? 'Save changes' : 'Create template')}
                    </button>
                </div>
            </form>
        </div>,
        document.body,
    );
};

export default TemplateEditorModal;
